import { useMemo, useCallback, useEffect, useState, useRef } from 'react'
import { Trans } from 'react-i18next'

import { Alert, Text, Button, Stack, Link } from '@vtex/shoreline'
import i18next from 'i18next'

import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { Countdown } from './Countdown'
import * as s from './style'

const COOKIE_KEY = 'ACKNOWLEDGE_DOMAIN_REDIRECT_v2'

export const shouldShowRedirectBanner = (function (): boolean {
  try {
    const key = 'shouldShowRedirectBanner'
    if (typeof window === 'undefined') return false
    return localStorage?.getItem(key) !== null
  } catch (err) {
    return false
  }
})()

export function RedirectDomainBanner() {
  const [ack, setAck] = useState<boolean>(false)
  const [shouldRedirect, setShouldRedirect] = useState<string>('')
  const { t } = useAPPTranslation()

  const hasAcknowledge = useMemo(() => {
    return _getCookie(COOKIE_KEY)
  }, [ack])

  const handleAck = useCallback(() => {
    _setCookie(COOKIE_KEY, 'true', 6)
    setAck(true)
  }, [hasAcknowledge])

  const { oldDomain, newDomain, isValidToRedirect } = useMemo(() => {
    const hostname =
      typeof window !== 'undefined' ? window.location.hostname : ''
    const parts = hostname.split('.')
    const subDomain = `${parts[0]}` !== 'app' ? `${parts[0]}.app` : parts[0]
    const isValidToRedirect =
      (parts.length >= 4 &&
        parts[1] === 'newtail' &&
        parts[2] === 'com' &&
        parts[3] === 'br') ||
      parts[2] === 'local'

    const oldDomain = isValidToRedirect ? hostname : null
    const newDomain = isValidToRedirect
      ? `${subDomain}.ads.vtex.com`
      : 'app.ads.vtex.com'

    return {
      isValidToRedirect,
      oldDomain,
      newDomain
    }
  }, [])

  const automaticRedirectDay = useMemo(() => {
    return new Date(2025, 6, 9)
  }, [])

  useEffect(() => {
    if (!isValidToRedirect) return
    const now = new Date()
    const isAfterRedirectDate = now >= automaticRedirectDay

    if (isAfterRedirectDate) {
      const currentPath = window.location.pathname + window.location.search
      const newURL = `https://${newDomain}${currentPath}`

      if (!window.location.hostname.includes(newDomain)) {
        setShouldRedirect(newURL)
      }
    }
  }, [isValidToRedirect])

  const formattedDate = new Intl.DateTimeFormat(i18next.language, {
    day: '2-digit',
    month: '2-digit'
  }).format(automaticRedirectDay)

  const countdownMS = 10000

  const { seconds, remaining } = _useCountdown(countdownMS, !!shouldRedirect)

  const percentage = Math.max(0, Math.min(100, (remaining / countdownMS) * 100))

  if (!isValidToRedirect) {
    return null
  }

  if (shouldRedirect && seconds === 0) {
    setTimeout(() => {
      window.location.replace(shouldRedirect)
    }, 2000)
  }

  if (shouldRedirect && isValidToRedirect) {
    return (
      <div
        className="position-fixed top-0 right-0 bottom-0 left-0 m-auto w-100 h-100 d-flex align-items-center justify-content-center"
        style={{ zIndex: 99999999, background: '#fff' }}
      >
        <s.RedirectingWrapper>
          <s.RedirectingTextBox>
            <s.RedirectingHeading>
              {t('c:components.RedirectDomainBanner.redirecting_title')}
            </s.RedirectingHeading>
            <Text variant="body" as="p">
              <Trans
                i18nKey="c:components.RedirectDomainBanner.redirecting"
                values={{ newDomain }}
                components={{ strong: <strong /> }}
              />
              <Link href={`https://${newDomain}`}>{newDomain}</Link>
            </Text>
          </s.RedirectingTextBox>
          <s.RedirectingAnimationBox>
            <s.WindowBoxGray elevate={seconds <= 0 ? 0 : 1}>
              {oldDomain}
            </s.WindowBoxGray>
            <s.CountdownWrapper>
              <Countdown
                style={
                  {
                    '--value': percentage || 0,
                    position: 'relative',
                    zIndex: 1
                  } as React.CSSProperties
                }
              >
                <s.CountdownSeconds>{seconds}</s.CountdownSeconds>
              </Countdown>
              <s.Arrow />
            </s.CountdownWrapper>
            <s.WindowBoxPink elevate={seconds <= 0 ? 2 : 1}>
              {newDomain}
            </s.WindowBoxPink>
          </s.RedirectingAnimationBox>
        </s.RedirectingWrapper>
      </div>
    )
  }

  if (hasAcknowledge) return null

  return (
    <s.Container style={{ maxWidth: '900px' }}>
      <Alert variant="informational">
        <Stack gap="xs">
          <Text variant="display4">
            {t('c:components.RedirectDomainBanner.title')}
          </Text>

          <Text variant="body">
            <Trans
              i18nKey="c:components.RedirectDomainBanner.description"
              values={{ date: formattedDate, oldDomain, newDomain }}
              components={{ strong: <strong /> }}
            />
          </Text>

          <Text variant="body">
            {t('c:components.RedirectDomainBanner.link_hint')}
            <Link href={`https://${newDomain}`}>{newDomain}</Link>
          </Text>
        </Stack>

        <Button variant="tertiary" onClick={handleAck}>
          {t('c:components.RedirectDomainBanner.ack')}
        </Button>
      </Alert>
    </s.Container>
  )
}

function _setCookie(name, value, hours) {
  const expires = new Date(Date.now() + hours * 3600e3).toUTCString()
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/`
}

function _getCookie(name) {
  const cookies = document.cookie
    ?.split('; ')
    ?.map(pair => pair?.split('='))
    ?.reduce((acc, [k, v]) => {
      acc[decodeURIComponent(k)] = decodeURIComponent(v)
      return acc
    }, {})
  return cookies[name] || null
}

function _useCountdown(ms: number, active: boolean = false) {
  const [remaining, setRemaining] = useState(ms)
  const startTimeRef = useRef<number | null>(null)
  const animationFrameRef = useRef<number>()

  const _default = { remaining: 0, seconds: 0, minutes: 0 }

  useEffect(() => {
    if (!active || ms <= 0) return

    startTimeRef.current = performance.now()
    setRemaining(ms)

    const tick = (now: number) => {
      if (!startTimeRef.current) return

      const elapsed = now - startTimeRef.current
      const timeLeft = Math.max(ms - elapsed, 0)

      setRemaining(timeLeft)

      if (timeLeft > 0) {
        animationFrameRef.current = requestAnimationFrame(tick)
      }
    }

    animationFrameRef.current = requestAnimationFrame(tick)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [ms, active])

  if (!active) return _default

  const seconds = Math.floor((remaining / 1000) % 60)
  const minutes = Math.floor((remaining / 1000 / 60) % 60)

  return { remaining, seconds, minutes }
}

export default RedirectDomainBanner
